<template>
  <div
    :class="[
      'page-list',
      'page-list--hamac-team'
    ]"
  >

    <div
      v-hide="
        fetchState.status.value !== 'success' ||
        providerCallCount === 0
      "
      class="page-list__subfilters"
    >

      <ui-wad class="font-weight-light font-size-90">
        <span
          v-if="context.total > 0"
        >
          <layout-misc-float :value="context.total" />
          {{
            context.total < 2 ?
              `équipe` :
              `équipes`
          }}
        </span>
        <span
          v-if="context.total === 0"
        >
          Aucune équipe trouvée
        </span>
      </ui-wad>

      <ui-button-link
        v-if="hasAnyCriterionSet"
        class="font-size-90"
        :variant="UI.VARIANT.SECONDARY"
        underline="hover"
        @click="resetCriterion"
      >
        <ui-icon
          name="filter_alt_off"
        />
        supprimer les filtres
      </ui-button-link>

    </div>


    <b-card
      :class="[
        'page-list__filters',
        fetchState.status.value !== 'success' ? 'page-list__filters--busy' : '',
      ]"
      body-class="p-1 d-flex justify-content-space-between"
    >

      <!-- Filters: first column -->
      <ui-wad class="d-flex align-items-center gap-1 flex-grow-1">

        <ui-form-inner-icon
          name="search"
          :size="UI.SIZE.SMALL"
          class="w-1/4"
        >
          <ui-form-input
            :model-value="context.search"
            :size="UI.SIZE.SMALL"
            placeholder="Rechercher"
            class="page-list-filters__search"
            @update:model-value="debouncedSearchChange"
          />
        </ui-form-inner-icon>

        <ui-chip-select-multiple
          v-model="context.filters.tags"
          :options="availableTags"
          hoistable
          searchable
        >
          Tags
        </ui-chip-select-multiple>

      </ui-wad>

      <!-- Filter: second column -->
      <ui-wad>
        <ui-tooltip-wrapper
          class="d-inline-block"
          v-bind="{
            title: `Vous n'avez pas les droits pour effectuer cette action`,
            variant: UI.VARIANT.LIGHT,
          }"
          :status="hasACL(ACLS.HAMAC_EQUIPE_CREATE) ?
            UI.STATUS.DISABLED :
            UI.STATUS.ENABLED
          "
          data-cy="add_team"
        >
          <layout-cta-sub
            component="ui-link-button"
            :variant="UI.VARIANT.PRIMARY"
            :route="hasACL(ACLS.HAMAC_EQUIPE_CREATE) ?
              getCreationRoute() :
              null
            "
            :size="UI.SIZE.SMALL"
            :status="hasACL(ACLS.HAMAC_EQUIPE_CREATE) ?
              UI.STATUS.ENABLED :
              UI.STATUS.DISABLED
            "
          >
            <ui-icon
              name="add"
            />
            &nbsp;
            Ajouter une équipe
          </layout-cta-sub>
        </ui-tooltip-wrapper>
      </ui-wad>

    </b-card>

    <ui-table-wrapper
      ref="table"
      :fields="fields"
      :initial-sort-by="context.order"
      :initial-sort-desc="context.sort === 'desc'"
      :provider="provider"
      hover
      striped
      fixed
      select-mode="range"
      @row-clicked="({ item }) => openDetailsIntent(item)"
    >
      <template v-if="providerCallCount === 0" #table-busy>
        <layout-placeholder-table-body
          :count="context.perPage"
          :size="UI.SIZE.MEDIUM"
          striped
        />
      </template>

      <template #cell(actions)="data">
        <div class="page-list__actions__inner">
          <ui-link-icon
            :class="[
              'page-list__open-cta',
              'font-size-20px'
            ]"
            name="arrow_outward"
            :route="getDetailsRoute(data.item)"
            :elevation="UI.ELEVATION.LEVEL_0"
            no-animation
          />
        </div>
      </template>

      <template #cell(tags)="data">
        <ui-wad class="d-flex flex-wrap gap-1">
          <b-badge
            v-for="(tag, index) in data.item.tags"
            :key="`${tag}-${index}`"
            :variant="UI.VARIANT.LIGHT"
            :class="[
              'page-list__tag-item',
              {
                'page-list__tag-item--tone-downed': isTagToneDowned(tag)
              }
            ]"
            href="#"
            @click.stop="toggleTag(tag)"
          >
            {{ tag }}
          </b-badge>
        </ui-wad>
      </template>

      <template #cell(member_count)="data">
        <span class="font-size-100 line-height-0 font-family-monospace w-3 text-align-right d-inline-block font-weight-bold">{{
          data.item.member_count
        }}</span>
        <ui-blank class="mr-1" />
        <span class="font-size-80 text-transform-uppercase color-gray-dark">
          {{
            data.item.member_count > 1 ?
              `utilisateurs` :
              `utilisateur`
          }}
        </span>
      </template>

    </ui-table-wrapper>


    <div class="page-list__footer">

      <ui-blank />

      <ui-table-pagination
        v-model="context.currentPage"
        :total-rows="context.total"
        :per-page="context.perPage"
        align="center"
      />

      <ui-wad class="d-flex align-items-center gap-1 flex-shrink-0">
        <ui-wad class="font-size-90 font-weight-light">
          par page
        </ui-wad>
        <ui-form-select
          v-model="context.perPage"
          :options="perPageOptions"
          :size="UI.SIZE.SMALL"
          class="page-list__filters__per-page"
        />
      </ui-wad>

    </div>

  </div>

  <nuxt-page />

</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/variables
@import ~/assets/sass/modules/table/members
@import ~/assets/sass/modules/form/variables

$actions-column-width: 6vw

.page-list
  $self: &

  padding-top: $layout-spacer-y * .75
  padding-left: $layout-spacer-x
  padding-right: $layout-spacer-x
  padding-bottom: $layout-spacer-y

  ///////////////////////////////////

  &__subfilters
    display: flex
    gap: $card-spacer-x
    align-items: baseline
    margin-bottom: $layout-spacer-y * .25
    padding-left: space-x(1.5)
    padding-right: space-x(1.5)
    animation: fade-in 150ms ease-in-out

  ///////////////////////////////////

  &__filters
    margin-bottom: $layout-spacer-y

    &--busy
      @include no-interaction
      opacity: 0.6
      transition: 0.3s
      transition-delay: 0.3s

    &__per-page
      width: auto
      font-size: 85%
      height: $simulated-form-control-height-md

  ///////////////////////////////////

  &__footer
    margin-top: $layout-spacer-y
    display: flex
    align-items: center
    justify-content: space-between

  ///////////////////////////////////

  &__tag-item
    @include precede
      font-size: 95%
      font-weight: $font-weight-normal

      &--tone-downed
        opacity: 0.25

  /////////////////////////////////

  :deep(.b-table.table)
    @include table-presets
    @include table-row-hovering( #{ $self }__open-cta )

    /// Columns
    #{ $self }__actions
      width: $actions-column-width

    /// Cells
    tbody

      #{ $self }__actions
        @include table-cell-presets

</style>

<script lang="ts">
import Console from '~/utils/lib/core/console'
import { any, append, applySpec, compose, debounce, equals, identity, isEmpty, map, not, reject, tapCatch, toString } from '~/utils/lib/core/prelude'
import { getDetailsRoute, getCreationRoute } from '~/stores/hamac/team'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import { useLinkHandler } from '~/composables/core/link-handler'
import type { UiTableWrapper } from '#components'
import { useTableProvider } from '~/composables/layout/table-provider'
import { type Team, type Filters, useStore_hamac_team_list, EVENTS } from '~/stores/hamac/team/list'
import { wrap as wrapInput } from '~/utils/infra/common/types'
import { useStore_hamac_team_tag } from '~/stores/hamac/team/tag'
import type { ComponentExposed } from 'vue-component-type-helpers'
import type { ChipSelectMultipleOption } from '~/components/ui/chip/SelectMultiple.vue'

const SEARCH_EXECUTION_DEBOUNCE_DELAY = 300

const console = new Console('component:company-overview')

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.EMBAUCHE,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: `Équipes — ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const ref_table
      = useTemplateRef<ComponentExposed<typeof UiTableWrapper>>('table')

    const tableProvider = useTableProvider<Team, Required<Filters>>(
      ref_table,
      {
        context: {
          order: 'createdAt',
          sort: 'desc',
          filters: {
            tags: []
          }
        },
      }
    )

    const store_team_list = useStore_hamac_team_list()
    const store_team_tag = useStore_hamac_team_tag()

    provide(
      EVENTS.ON_TEAM_LIST_REFRESH_NEEDED,
      () => tableProvider.refreshTable()
    )

    return {
      ...navigationContext,
      ...useAccessControl(),
      ...useLinkHandler(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      ...tableProvider,
      store_team_tag,
      store_team_list,
    }
  },
  data() {
    return {
      availableTags: [] as ChipSelectMultipleOption<Team['tags'][number]>[],
      fields: [
        {
          key: 'actions',
          label: '',
          class:'page-list__actions'
        },
        {
          key: 'label',
          label: 'Nom',
          sortable: true,
        },
        {
          key: 'tags',
          label: 'Tags',
        },
        {
          key: 'member_count',
          label: 'Membres',
        },
      ],
      perPageOptions: [
        {
          value: 10,
          text: '10'
        },
        {
          value: 30,
          text: '30',
        },
        {
          value: 50,
          text: '50'
        }
      ],
      debouncedSearchChange: null as unknown as (_: string) => void, // (lazy)
    }
  },
  created() {

    this.fetchWrapper(
      () => {
        const uuidData = this.fromCompanyLevel().structure.uuidData
        const code_societe = this.fromCompanyLevel().company.code_societe

        return this.store_team_tag.read(
            { uuidData, code_societe }
          )
          .then(results => this.availableTags = map.iterable(
            applySpec({
              value: identity,
              text: identity
            }),
            results
          ))
          .catch(tapCatch(err => {
            console.warn('fetch', err)
            this.ToastBuilder
              .error(toString(err))
              .title(`Liste des équipes`)
              .show()
          }))
      }
    )

    // @ts-ignore: TypeScript wrong infered
    this.consumer = (context) => {
      const options = {
        input: wrapInput(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      return this.store_team_list.read(options)
        .catch(tapCatch(err => {
          console.warn('searchCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Liste des équipes`)
            .show()
        }))
    }

    this.debouncedSearchChange = debounce(
      SEARCH_EXECUTION_DEBOUNCE_DELAY,
      this.setSearch
    )
  },
  methods: {
    getCreationRoute() {
      const {
        structure: { id },
        company: { code_societe }
      } = this.fromCompanyLevel()

      return getCreationRoute(id, code_societe)
    },
    getDetailsRoute({ uuid_hamac_equipe }) {
      const {
        structure: { id },
        company: { code_societe }
      } = this.fromCompanyLevel()

      return getDetailsRoute(id, code_societe, uuid_hamac_equipe)
    },
    openDetailsIntent({ uuid_hamac_equipe }) {
      const url = this.getDetailsRoute({ uuid_hamac_equipe })

      this.goToRoute(url)
    },
    isTagSelected(tag) {
      return any(equals(tag), this.context.filters.tags)
    },
    isTagToneDowned(tag) {
      if (isEmpty(this.context.filters.tags))
        return false

      return compose(not, this.isTagSelected)(tag)
    },
    toggleTag(tag) {
      const has = this.isTagSelected(tag)

      this.context.filters.tags = has ?
        reject(equals(tag), this.context.filters.tags) :
        append(tag, this.context.filters.tags)
    }
  }
})
</script>
